import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Menu, X, ArrowRight } from "lucide-react";
import { useState } from "react";
import Logo from "@/components/Logo";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

const FOOTER_PRODUCT = [
  { to: "/", label: "Home" },
  { to: "/pricing", label: "Pricing" },
  { to: "/about", label: "About" },
];
const FOOTER_LEGAL = [
  { to: "/terms", label: "Terms of Service" },
  { to: "/privacy", label: "Privacy Policy" },
  { to: "/refund", label: "Refund Policy" },
  { to: "/contact", label: "Contact Us" },
];

export default function MarketingShell({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const loc = useLocation();

  // Reset menu on route change
  if (open && loc.pathname !== open) {
    /* noop */
  }

  return (
    <div className="min-h-screen flex flex-col bg-white text-zinc-900">
      {/* Top nav */}
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-5 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="marketing-logo">
            <Logo className="h-10 w-auto" />
          </Link>

          <nav className="hidden md:flex items-center gap-7">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                data-testid={`marketing-nav-${n.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-medium hover:text-zinc-900 transition-colors ${
                    isActive ? "text-zinc-900" : "text-zinc-600"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            {user ? (
              <Link
                to="/dashboard"
                data-testid="marketing-open-app"
                className="bg-zinc-900 text-white px-4 py-2 text-sm font-semibold hover:bg-zinc-800 flex items-center gap-1.5"
              >
                Open App <ArrowRight size={14} />
              </Link>
            ) : (
              <>
                <Link to="/login" data-testid="marketing-signin" className="text-sm font-medium text-zinc-700 hover:text-zinc-900">
                  Sign in
                </Link>
                <Link
                  to="/register"
                  data-testid="marketing-cta-top"
                  className="bg-zinc-900 text-white px-4 py-2 text-sm font-semibold hover:bg-zinc-800 flex items-center gap-1.5"
                >
                  Start free trial <ArrowRight size={14} />
                </Link>
              </>
            )}
          </div>

          <button
            className="md:hidden text-zinc-900"
            onClick={() => setOpen(!open)}
            data-testid="marketing-menu-toggle"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>

        {open && (
          <div className="md:hidden border-t border-zinc-200 bg-white px-5 py-4 space-y-3">
            {NAV.map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setOpen(false)}
                className="block text-sm font-medium text-zinc-700"
              >
                {n.label}
              </Link>
            ))}
            <div className="border-t border-zinc-200 pt-3 flex gap-3">
              {user ? (
                <Link to="/dashboard" className="bg-zinc-900 text-white px-4 py-2 text-sm font-semibold flex-1 text-center">
                  Open App
                </Link>
              ) : (
                <>
                  <Link to="/login" className="text-sm font-medium text-zinc-700 px-3 py-2 flex-1 text-center border border-zinc-300">
                    Sign in
                  </Link>
                  <Link to="/register" className="bg-zinc-900 text-white px-4 py-2 text-sm font-semibold flex-1 text-center">
                    Start trial
                  </Link>
                </>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1">{children}</main>

      {/* Footer */}
      <footer className="bg-zinc-950 text-zinc-300 mt-20">
        <div className="max-w-7xl mx-auto px-5 py-14 grid grid-cols-1 md:grid-cols-12 gap-8">
          <div className="md:col-span-5">
            <Logo className="h-12 w-auto" variant="horizontal" />
            <p className="mt-4 text-sm text-zinc-400 leading-relaxed max-w-md">
              TAXINPHO is the all-in-one practice management OS built for Indian
              Chartered Accountants. Manage clients, file returns, raise GST invoices,
              and grow your firm — powered by AI.
            </p>
            <p className="mt-4 text-xs text-zinc-500">
              A registered trade-licensed business of <strong className="text-zinc-300">Nirmala Agrawal (Proprietor)</strong>,
              Balangir, Odisha. License No: TL/BLR/2023-07-14/033462
            </p>
          </div>
          <div className="md:col-span-3">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Product</p>
            <ul className="space-y-2 text-sm">
              {FOOTER_PRODUCT.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white">{l.label}</Link>
                </li>
              ))}
              <li><Link to="/register" className="hover:text-white">Start free trial</Link></li>
              <li><Link to="/login" className="hover:text-white">Sign in</Link></li>
            </ul>
          </div>
          <div className="md:col-span-4">
            <p className="text-xs font-bold uppercase tracking-widest text-zinc-500 mb-4">Legal &amp; Contact</p>
            <ul className="space-y-2 text-sm">
              {FOOTER_LEGAL.map((l) => (
                <li key={l.to}>
                  <Link to={l.to} className="hover:text-white" data-testid={`footer-${l.to.replace("/", "")}`}>{l.label}</Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-5 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-zinc-500">
            <p>© {new Date().getFullYear()} TAXINPHO · Proprietor: Nirmala Agrawal. All rights reserved.</p>
            <p>Made in India 🇮🇳 · Built for Chartered Accountants</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
